<script setup lang="ts">
/**
 * The trace list, beside the trace it has open.
 *
 * `ListDetailLayout` owns the split, its persistence and the pager, so this file is filters plus a
 * selection. Most operators arrive here pre-filtered from an order or a job and never type anything;
 * the search box is the secondary path.
 */
import { computed, inject, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import {
  DropdownItem,
  DropdownMenu,
  InstantRangeInput,
  ListDetailLayout,
  ToggleSwitch,
  type InstantRangeValue,
} from '@hantera/design-system'
import { useLocalization } from '@hantera/portal-app'
import type { TraceStatus } from '@/backend/traces.types'
import { openTraceIdKey } from './traces-view-context'
import useTracesViewContext from './use-traces-view-context'
import { TracesDataProvider } from './traces-data-provider'
import TraceView from './TraceView.vue'

const t = useLocalization('monitoring')

const traces = useTracesViewContext()

const provider = new TracesDataProvider(traces, t)

/**
 * A trace a cross-link asked to open, adopted as the initial selection.
 *
 * The list is deliberately not filtered to it: an operator arriving from a job wants their trace
 * open, with the surrounding traces still there to compare it against.
 *
 * Its row need not be on the loaded page for this to work — `ListDetailLayout` keeps an off-page
 * selection open, and `TraceView` falls back to the selection when no row accompanies it.
 */
const openTraceId = inject(openTraceIdKey, ref(undefined))

/** The open trace, as its id. `TracesDataProvider` keys rows by `traceId`, so no mapping is needed. */
const selection = ref<unknown>(openTraceId.value)

const searchTerm = ref('')
const autoRefresh = ref(true)

/**
 * The resource classes that can appear in a trace, offered on the first path segment so the syntax
 * is discoverable rather than folklore.
 */
const RESOURCE_PREFIXES = ['actors/', 'egresses/', 'ingresses/', 'jobs/', 'rules/', 'sendings/']

const suggestions = computed(() => {
  const term = searchTerm.value.trim()
  if (!term || term.includes('/')) return []
  return RESOURCE_PREFIXES.filter((prefix) => prefix.startsWith(term.toLowerCase()))
})

/**
 * Offered through a dropdown rather than an absolutely-positioned list: the toolbar the search box
 * sits in is a scroll container, which would clip a list positioned within it.
 *
 * A popover rather than a modal dropdown, so opening it does not steal focus from the box still being
 * typed into.
 */
const searchElem = useTemplateRef<HTMLInputElement>('searchElem')
const suggestionsMenu = useTemplateRef<InstanceType<typeof DropdownMenu>>('suggestionsMenu')

watch(suggestions, (list) => {
  if (list.length > 0) {
    suggestionsMenu.value?.open()
  } else {
    suggestionsMenu.value?.close()
  }
})

const dateRange = computed<InstantRangeValue | undefined>({
  get: () => {
    const range = traces.dateRange
    return range == null
      ? undefined
      : {
          from: range.from,
          to: range.to,
          rolling: traces.rolling ?? undefined,
        }
  },
  set: (value: InstantRangeValue | undefined) => traces.setRange(value ?? null),
})

/**
 * Polling a fixed range can only ever turn up traces written after the fact, since its end is
 * already in the past. Disabled rather than hidden, so the dependency between the two controls is
 * visible; the checkbox keeps its state and resumes when a rolling range is chosen again.
 */
const canAutoRefresh = computed(() => traces.rolling != null)

const statusFilter = computed({
  get: () => traces.filters.status ?? 'all',
  set: (value: TraceStatus | 'all') => {
    traces.filters.status = value === 'all' ? null : value
  },
})


function submitSearch() {
  const term = searchTerm.value.trim()
  if (!term) return

  traces.addTerm(term)
  searchTerm.value = ''
  traces.refresh()
}

function applySuggestion(prefix: string) {
  searchTerm.value = prefix
  searchElem.value?.focus()
}

function removeResource(term: string) {
  traces.removeTerm(term)
  traces.refresh()
}

function clearTraceId() {
  traces.clearTraceId()
  traces.refresh()
}

watch(
  [() => traces.dateRange?.from, () => traces.dateRange?.to, () => traces.filters.status],
  () => traces.refresh(),
)


let refreshInterval: ReturnType<typeof setInterval> | null = null

/**
 * Polls only while the tab is visible — a hidden tab has nobody watching.
 *
 * Polling continues while a trace is open, since the list stays on screen beside the detail and a
 * silently frozen list is worse than one that reorders: `pollNewer()` merges rather than replaces,
 * and the layout keeps the detail open even when a refresh prunes the selected row from the page.
 */
function shouldPoll(): boolean {
  return autoRefresh.value && canAutoRefresh.value && document.visibilityState === 'visible'
}

function startPolling() {
  stopPolling()
  refreshInterval = setInterval(() => {
    if (shouldPoll()) {
      traces.pollNewer()
    }
  }, 30_000)
}

function stopPolling() {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

onMounted(() => {
  traces.refresh()
  startPolling()
  document.addEventListener('visibilitychange', startPolling)
})

onUnmounted(() => {
  stopPolling()
  document.removeEventListener('visibilitychange', startPolling)
})
</script>

<template>
  <!--
    Single root, deliberately. `TabPanel` mounts every tab at once and hides the inactive ones with
    `v-show`, which sets `display: none` on a component's one root element — a multi-root component
    is a fragment and cannot be hidden at all, so it renders over whichever tab is selected.
  -->
  <div class="traces-tab">
    <ListDetailLayout
      :provider="provider"
      :detail-component="TraceView"
      v-model:selection="selection"
      cache-key="monitoring-traces"
    >
      <template #list-toolbar>
        <div class="filters">
          <div class="controls">
            <input
              ref="searchElem"
              v-model="searchTerm"
              class="search-input"
              type="search"
              :placeholder="t('traces.searchPlaceholder')"
              @keyup.enter="submitSearch"
            />

            <InstantRangeInput v-model="dateRange" rolling all-time class="range" />

            <select v-model="statusFilter" class="status-select">
              <option value="all">{{ t('traces.filters.allOutcomes') }}</option>
              <option value="failed">{{ t('traces.filters.failedOnly') }}</option>
              <option value="partial">{{ t('traces.filters.partialOnly') }}</option>
            </select>

            <label
              class="auto-refresh"
              :class="{ 'auto-refresh--disabled': !canAutoRefresh }"
              :title="canAutoRefresh ? undefined : t('traces.filters.autoRefreshFixed')"
            >
              <ToggleSwitch v-model="autoRefresh" :disabled="!canAutoRefresh" />
              {{ t('traces.filters.autoRefresh') }}
            </label>
          </div>

          <!-- Chips accumulate as repeated `resource` parameters, so an operator can start from an
               order and narrow to AvaTax without composing a query by hand. -->
          <div v-if="traces.filters.resources.length > 0 || traces.filters.traceId" class="chips">
            <button
              v-if="traces.filters.traceId"
              class="chip btn-reset"
              type="button"
              @click="clearTraceId"
            >
              {{ traces.filters.traceId }} ✕
            </button>
            <button
              v-for="term in traces.filters.resources"
              :key="term"
              class="chip btn-reset"
              type="button"
              @click="removeResource(term)"
            >
              {{ term }} ✕
            </button>
          </div>
        </div>
      </template>
    </ListDetailLayout>

    <DropdownMenu ref="suggestionsMenu" popover :target="searchElem">
      <DropdownItem v-for="prefix in suggestions" :key="prefix" @click="applySuggestion(prefix)">
        <span class="suggestion">{{ prefix }}</span>
      </DropdownItem>
    </DropdownMenu>
  </div>
</template>

<style scoped>
/* `ListDetailLayout` sizes itself to its parent, so the wrapper has to carry the panel's height. */
.traces-tab {
  display: grid;
  grid-template-rows: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

/* The layout's `.toolbar` is a single nowrap row, so both rows live in one wrapper. */
.filters {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.search-input {
  flex: 1;
  min-width: 180px;
  padding: 4px 8px;
  border: 1px solid hsl(var(--color-70));
  border-radius: 4px;
  background-color: hsl(var(--color-100));
  color: inherit;
  font: inherit;
  font-size: 12px;
}

.range {
  width: 220px;
}

.suggestion {
  font-family: monospace;
  font-size: 11px;
}

.status-select {
  padding: 4px 6px;
  border: 1px solid hsl(var(--color-70));
  border-radius: 4px;
  background-color: hsl(var(--color-100));
  color: inherit;
  font-size: 12px;
}

.auto-refresh {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: hsl(var(--color-40));
  white-space: nowrap;
}

.auto-refresh--disabled {
  opacity: 0.5;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chip {
  padding: 2px 6px;
  border: 1px solid hsl(var(--color-70));
  border-radius: 10px;
  background-color: hsl(var(--color-95));
  font-family: monospace;
  font-size: 11px;
  cursor: pointer;
}

.chip:hover {
  background-color: hsl(var(--color-90));
}
</style>
